import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { GroupResponse, GroupBalance, ActivityResponse, GroupDetailResponse, GroupMember } from '../types/group';

export interface Group extends Omit<GroupResponse, 'unread_count'> {
  recent_activity: ActivityResponse[];
  unread_activity_count: number;
  unread_messages_count: number;
}

interface UseGroupOptions {
  isVisible?: boolean;
}

export const useGroup = (
  groupId: string | number | undefined,
  accessToken: string,
  userId?: string,
  options: UseGroupOptions = {}
) => {
  const { isVisible = true } = options;
  const queryClient = useQueryClient();

  // Use React Query to fetch group data
  const {
    data: group,
    isLoading: loading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['group', groupId],
    queryFn: async (): Promise<Group | null> => {
      if (!accessToken || !groupId) return null;

      try {
        console.log(`Fetching group ${groupId} details...`);
        const response = await fetch(`${env.API_URL}/api/groups/${groupId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });

        // Even if the response is not OK, try to parse the error message
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          // If we have error details from the server, log them
          if (data && data.detail) {
            console.error('Server error details:', data.detail);
          }

          // If the status is 404, we'll create a fallback group with minimal data
          // This allows the UI to still function while showing an error message
          if (response.status === 404) {
            console.log('Group not found, creating fallback data');

            // Create a minimal fallback group
            const fallbackGroup: Group = {
              id: Number(groupId),
              name: 'Group Details Unavailable',
              description: 'There was an error loading this group. Please try again later.',
              currency_code: 'USD',
              owner_id: userId || '',
              created_at: new Date().toISOString(),
              members: [],
              balances: [],
              total_expenses: 0,
              recent_activity: [],
              unread_activity_count: 0,
              unread_messages_count: 0
            };

            return fallbackGroup;
          }

          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // If we have valid data, process it
        if (data) {
          // Find the current user's member record to get the last_viewed_at timestamp
          const currentMember = data.group?.members?.find((m: GroupMember) => m.user_id === userId);
          const lastViewedAt = currentMember?.last_viewed_at ? new Date(currentMember.last_viewed_at) : null;

          console.log('[useGroup] Current user member data:', {
            userId,
            memberFound: !!currentMember,
            memberId: currentMember?.id,
            lastViewedAt: lastViewedAt?.toISOString() || 'none',
            allMembersCount: data.group?.members?.length || 0,
            allMembers: data.group?.members?.map((m: GroupMember) => ({
              id: m.id,
              email: m.email.substring(0, 3) + '...', // Truncate email for privacy in logs
              userId: m.user_id ? (m.user_id === userId ? 'current_user' : 'other') : 'none',
              hasLastViewedAt: !!m.last_viewed_at
            }))
          });

          // Calculate unread activity count based on last_viewed_at timestamp
          let unreadCount = 0;
          if (lastViewedAt && data.recent_activity) {
            unreadCount = data.recent_activity.filter((a: ActivityResponse) => {
              const activityDate = new Date(a.created_at);
              const isUnread = activityDate > lastViewedAt;
              if (isUnread) {
                console.log(`[useGroup] Activity ${a.id} (${a.type}) is unread: ${a.created_at} > ${lastViewedAt}`);
              }
              return isUnread;
            }).length;

            console.log(`[useGroup] Calculated ${unreadCount} unread activities based on lastViewedAt`);
          } else if (data.recent_activity) {
            // Fallback: if no last_viewed_at, consider all activities as unread
            unreadCount = data.recent_activity.length;
            console.log(`[useGroup] No lastViewedAt found, marking all ${unreadCount} activities as unread`);
          }

          // Ensure we have all required fields with fallbacks
          const groupData: Group = {
            ...data.group,
            id: data.group?.id || Number(groupId),
            name: data.group?.name || 'Unnamed Group',
            description: data.group?.description || '',
            currency_code: data.group?.currency_code || 'USD',
            owner_id: data.group?.owner_id || userId || '',
            created_at: data.group?.created_at || new Date().toISOString(),
            members: data.group?.members || [],
            balances: data.balances || [],
            total_expenses: data.group?.total_expenses || 0,
            recent_activity: data.recent_activity || [],
            unread_activity_count: unreadCount,
            unread_messages_count: 0 // This would come from a messages endpoint
          };

          // Debug logging for group data
          console.log('Raw group data:', data);
          console.log('Processed balances:', groupData.balances);

          return groupData;
        } else {
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        console.error('Error fetching group:', err);
        throw err;
      }
    },
    enabled: isVisible && !!accessToken && !!groupId,
  });

  // Convert React Query error to string format for API compatibility
  const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

  // Leave group mutation
  const leaveGroupMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken || !groupId) {
        throw new Error('Authentication required');
      }

      console.log(`[useGroup] Leaving group ${groupId}...`);
      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `Failed to leave group (${response.status})`;
        console.error(`[useGroup] Error leaving group: ${errorMessage}`);
        throw new Error(errorMessage);
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate groups queries
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
    },
  });

  return {
    group,
    loading,
    error,
    refresh: refetch,
    leaveGroup: leaveGroupMutation.mutateAsync,
  };
}; 